import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const PASSWORD_PLACEHOLDER = "__TINES_MYSQL_PASSWORD__";
const DEFAULT_PORT = "3306";
const DEFAULT_DATABASE = "mysql";

function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9.-]+$/.test(value)) {
    throw new Error("Invalid MySQL host");
  }
  return value;
}

function portComponent(port: string | undefined) {
  const value = (port || DEFAULT_PORT).trim();
  if (!/^\d+$/.test(value)) throw new Error("Invalid MySQL port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new Error("Invalid MySQL port");
  }
  return value;
}

function databaseComponent(database: string | undefined) {
  return database?.trim() || DEFAULT_DATABASE;
}

function connectionString({
  host,
  port,
  username,
  database,
  tls,
}: Record<string, string>) {
  const mysqlHost = hostComponent(host);
  const mysqlPort = portComponent(port);
  const mysqlDatabase = databaseComponent(database);
  const encodedUsername = encodeURIComponent(username ?? "");
  const params = new URLSearchParams();
  if (tls === "true") {
    params.set("ssl", JSON.stringify({ rejectUnauthorized: true }));
  }
  const query = params.size > 0 ? `?${params}` : "";
  return `mysql://${encodedUsername}:${PASSWORD_PLACEHOLDER}@${mysqlHost}:${mysqlPort}/${encodeURIComponent(mysqlDatabase)}${query}`;
}

export const mysql = defineConnector({
  identifier: "mysql",
  name: "MySQL",
  tagLine: "Relational database",
  logo,
  tags: ["data"],
  protocol: "mysql",
  connectionTest: { protocol: "mysql", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("mysql"),
  steps: [
    {
      title: "Enter your MySQL connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "MySQL",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "db.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "3306",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Database",
          required: true,
          defaultValue: "mysql",
          target: { kind: "configuration", field: "database" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "false",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "false", label: "Disabled" },
            { value: "true", label: "Enabled" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload a PEM CA certificate for private or self-signed MySQL servers.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: {
            target: { kind: "configuration", field: "tls" },
            value: "true",
          },
        },
      ],
    },
  ],
  publicEnvVars: (configuration) => ({
    MYSQL_URL: connectionString(configuration),
  }),
  usageHint: () => "Use process.env.MYSQL_URL as the MySQL connection string.",
});
